import { addDoc, deleteDoc, doc, getDoc, getDocs, query, updateDoc } from 'firebase/firestore';
import { buildInputsById, getRecipeCost, Recipe, recipesRef } from '@api/Recipes/Recipe';
import { Input } from '@api/Inputs/Input';
import InputServ from '@api/Inputs/InputServ';
import ModifierGroupServ from '@api/ModifierGroups/ModifierGroupServ';
import DishServ from '@api/Dishes/DishServ';
import ActivityLogServ from '@api/ActivityLogs/ActivityLogServ';
import { buildCreationStamp } from '@api/ActivityLogs/CreationStamp';
import { logCatalogChange, RECIPE_FIELDS } from '@api/ActivityLogs/CatalogActivity';
import { publishCatalog } from '@api/Catalog/CatalogVersion';

export default {
    async saveRecipe(recipe: Recipe): Promise<Recipe> {
        // Firestore no acepta undefined: normalizamos items y flags antes de escribir
        recipe.items = (recipe.items || []).map((item) => ({
            inputId: item.inputId,
            quantity: Number(item.quantity) || 0,
            optional: item.optional ?? false
        }));
        recipe.availableForModifiers = recipe.availableForModifiers ?? false;
        recipe.status = recipe.status ?? true;
        recipe.cost = await this.getRecipeCostFromInputs(recipe);
        const isNew = !recipe.id;
        const before = isNew ? null : await this.getRecipe(recipe.id as string);
        if (isNew) {
            const data = await addDoc(recipesRef, { ...recipe, ...(await buildCreationStamp()) });
            recipe.id = data.id;
        }
        await updateDoc(doc(recipesRef, recipe.id), { ...recipe });
        await publishCatalog('recipe');
        await logCatalogChange('recipe', recipe.id, before, recipe, RECIPE_FIELDS);
        return recipe;
    },
    // El costo derivado SIEMPRE se recalcula contra los insumos vigentes (mismo patrón que InputServ.saveInput)
    async getRecipeCostFromInputs(recipe: Recipe): Promise<number> {
        const uniqueInputIds = Array.from(new Set(recipe.items.map((item) => item.inputId).filter(Boolean)));
        const inputs: Input[] = [];
        await Promise.all(
            uniqueInputIds.map(async (inputId) => {
                const input = await InputServ.getInput(inputId);
                if (input) {
                    inputs.push(input);
                }
            })
        );
        return getRecipeCost(recipe.items, buildInputsById(inputs));
    },
    async getRecipes(): Promise<Recipe[]> {
        const recipes: Recipe[] = [];
        const queryDoc = query(recipesRef);
        const snapshot = await getDocs(queryDoc);
        if (snapshot.empty) {
            return recipes;
        }
        snapshot.docs.map((doc) => {
            const recipe = doc.data() as Recipe;
            recipe.id = doc.id;
            recipes.push(recipe);
        });
        recipes.sort((a, b) => a.name.localeCompare(b.name, 'es'));
        return recipes;
    },
    async getRecipe(recipeId: string): Promise<Recipe> {
        const documentData = await getDoc(doc(recipesRef, recipeId));
        const recipe = documentData.data() as Recipe;
        if (recipe) {
            recipe.id = recipeId;
        }
        return recipe;
    },
    async deleteRecipe(recipeId: string): Promise<void> {
        // Candado de integridad: no dejar recetas huérfanas referenciadas por modificadores
        // o productos (el catálogo viejo acumuló ~70 opciones apuntando a insumos borrados)
        const [groupsUsingRecipe, dishesUsingRecipe] = await Promise.all([
            ModifierGroupServ.getModifierGroupsByRecipeId(recipeId),
            DishServ.getDishesByRecipeId(recipeId)
        ]);
        if (groupsUsingRecipe.length > 0 || dishesUsingRecipe.length > 0) {
            const usedBy = [
                ...groupsUsingRecipe.map((group) => `modificador "${group.name}"`),
                ...dishesUsingRecipe.map((dish) => `producto "${dish.name}"`)
            ].join(', ');
            throw new Error(`La receta está en uso por: ${usedBy}. Quita esas referencias antes de eliminarla.`);
        }
        await deleteDoc(doc(recipesRef, recipeId));
        await publishCatalog('recipe-delete');
        await ActivityLogServ.addActivityLog('recipe', recipeId, 'eliminó la receta');
        return;
    }
};
